import { CERTIFIED_METRICS } from "portal/utils/certifiedMetrics";
import { DailyMetricResponse } from "protos/portal/metrics";
import { isUndefined } from "portal/utils/identity";
import { mean, sum } from "portal/utils/math";
import type { Aggregates, ProcessedMetrics } from "./table.types";

/**
 * Computes total, average, and unanimous values per certified metric over a set
 * of `ProcessedMetrics` rows (robot-level subtotals and whole-table footer).
 */
export const getAggregates = (
  rows: ProcessedMetrics[],
  baseFields: Partial<DailyMetricResponse>
): Aggregates => {
  const template = {
    ...baseFields,
    db: undefined,
  } as ProcessedMetrics;
  const totals = { ...template };
  const averages = { ...template };
  const unanimous = { ...template };

  for (const metric of CERTIFIED_METRICS) {
    if (metric.type === "number" && !metric.canTotal && !metric.canAverage) {
      continue;
    }
    const numbers: number[] = [];
    let firstRow = true;
    let unanimousValue: boolean | number | string | undefined;
    for (const row of rows) {
      const value = row[metric.id];
      if (firstRow) {
        firstRow = false;
        unanimousValue = value;
      }

      if (
        typeof value === "number" &&
        !Number.isNaN(value) &&
        !isUndefined(value)
      ) {
        numbers.push(value);
      }
      if (unanimousValue !== value) {
        unanimousValue = undefined;
        // if it's not a number and we know the values are mixed, we don't
        // have to look at all of them
        if (metric.type !== "number") {
          break;
        }
      }
    }
    if (numbers.length > 0) {
      // not sure how to get these types to work correctly
      (totals as any)[metric.id] = metric.canTotal ? sum(numbers) : undefined;
      (averages as any)[metric.id] = metric.canAverage
        ? mean(numbers)
        : undefined;
    }
    (unanimous as any)[metric.id] = unanimousValue;
  }
  return { totals, averages, unanimous };
};
